/* AUTH003D_HARDENED */

import axios from "axios";

import {
  COGNITO_DOMAIN,
  COGNITO_CLIENT_ID
} from "../authConfig";

const REGION =
  process.env.REACT_APP_COGNITO_REGION || "us-east-1";

const IDLE_MS = 30 * 60 * 1000;
const REFRESH_MARGIN = 120000;

const ACTIVITY_KEY = "ipdepot:activity";
const SOURCE_KEY = "ipdepot:refreshSource";
const DEVICE_KEY = "ipdepot:deviceKey";

let locked = false;
let refreshPromise = null;
let generation = 0;

const listeners = new Set();
const waiters = new Set();
const installed = new WeakSet();

function emit() {
  for (const listener of listeners) {
    listener(locked);
  }
}

export function subscribeSession(listener) {
  listeners.add(listener);
  listener(locked);

  return () => listeners.delete(listener);
}

export function isSessionLocked() {
  return locked;
}

export function lockEnterpriseSession() {
  if (locked) return;

  locked = true;
  generation++;
  emit();
}

export function unlockEnterpriseSession() {
  locked = false;
  generation++;

  sessionStorage.setItem(
    ACTIVITY_KEY,
    String(Date.now())
  );

  emit();

  for (const resolve of waiters) resolve();
  waiters.clear();

  window.dispatchEvent(
    new Event("ipdepot:session-restored")
  );
}

export function waitUntilUnlocked() {
  if (!locked) return Promise.resolve();

  return new Promise(resolve => waiters.add(resolve));
}

export function markSessionActivity() {
  if (locked) return;

  sessionStorage.setItem(
    ACTIVITY_KEY,
    String(Date.now())
  );
}

export function isIdle() {
  const value = Number(
    sessionStorage.getItem(ACTIVITY_KEY)
  );

  if (!Number.isFinite(value) || value <= 0) {
    markSessionActivity();
    return false;
  }

  return Date.now() - value >= IDLE_MS;
}

export function readClaims(token) {
  try {
    const payload = token.split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    return JSON.parse(atob(payload));
  } catch {
    return {};
  }
}

export function currentIdentity() {
  const id = readClaims(
    localStorage.getItem("idToken") || ""
  );

  const access = readClaims(
    localStorage.getItem("accessToken") || ""
  );

  return {
    sub: id.sub || access.sub || "",
    username:
      access.username ||
      id["cognito:username"] ||
      "",
    display:
      id.email ||
      access.username ||
      id["cognito:username"] ||
      ""
  };
}

function expiration() {
  const access = readClaims(
    localStorage.getItem("accessToken") || ""
  );

  const id = readClaims(
    localStorage.getItem("idToken") || ""
  );

  const a = Number(access.exp) || 0;
  const b = Number(id.exp) || 0;

  if (!a || !b) return 0;

  return Math.min(a, b) * 1000;
}

function currentTokens() {
  return {
    accessToken: localStorage.getItem("accessToken") || "",
    idToken: localStorage.getItem("idToken") || ""
  };
}

export function saveEnterpriseTokens(tokens) {
  const access = tokens.accessToken;
  const id = tokens.idToken;

  if (!access || !id) {
    throw new Error("Tokens incompletos.");
  }

  const previous = currentIdentity();

  const a = readClaims(access);
  const i = readClaims(id);

  if (!i.sub || !a.sub || i.sub !== a.sub) {
    throw new Error("Identidad de tokens inconsistente.");
  }

  if (previous.sub && previous.sub !== i.sub) {
    throw new Error("Cambio de identidad detectado.");
  }

  if (
    !a.exp ||
    !i.exp ||
    Math.min(a.exp, i.exp) * 1000 <= Date.now()
  ) {
    throw new Error("Tokens expirados o invalidos.");
  }

  localStorage.setItem("accessToken", access);
  localStorage.setItem("idToken", id);

  if (tokens.refreshToken) {
    localStorage.setItem(
      "refreshToken",
      tokens.refreshToken
    );
  }

  window.dispatchEvent(
    new Event("ipdepot:tokens-updated")
  );
}

async function refreshOAuth(refreshToken) {
  const params = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: COGNITO_CLIENT_ID,
    refresh_token: refreshToken
  });

  const response = await fetch(
    `${COGNITO_DOMAIN}/oauth2/token`,
    {
      method: "POST",
      headers: {
        "Content-Type":
          "application/x-www-form-urlencoded"
      },
      body: params
    }
  );

  if (!response.ok) {
    throw new Error("OAuth refresh rechazado.");
  }

  const data = await response.json();

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token || refreshToken
  };
}

async function refreshDirect(refreshToken) {
  const deviceKey = localStorage.getItem(DEVICE_KEY);

  const body = {
    ClientId: COGNITO_CLIENT_ID,
    RefreshToken: refreshToken
  };

  if (deviceKey) body.DeviceKey = deviceKey;

  const response = await fetch(
    `https://cognito-idp.${REGION}.amazonaws.com/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target":
          "AWSCognitoIdentityProviderService.GetTokensFromRefreshToken"
      },
      body: JSON.stringify(body)
    }
  );

  const data = await response.json();

  if (!response.ok || !data.AuthenticationResult) {
    throw new Error("Cognito direct refresh rechazado.");
  }

  const result = data.AuthenticationResult;

  if (result.NewDeviceMetadata?.DeviceKey) {
    localStorage.setItem(
      DEVICE_KEY,
      result.NewDeviceMetadata.DeviceKey
    );
  }

  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken || refreshToken
  };
}

async function renew() {
  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    throw new Error("No existe refresh token.");
  }

  const source = localStorage.getItem(SOURCE_KEY);

  if (source === "direct") {
    return refreshDirect(refreshToken);
  }

  if (source === "oauth" || !source) {
    return refreshOAuth(refreshToken);
  }

  throw new Error("Origen del token desconocido.");
}

export async function ensureEnterpriseSession(force = false) {
  if (isIdle()) lockEnterpriseSession();

  if (locked) {
    await waitUntilUnlocked();
  }

  if (
    !force &&
    expiration() > Date.now() + REFRESH_MARGIN
  ) {
    return;
  }

  if (!refreshPromise) {
    const started = generation;

    const task = (async () => {
      const tokens = await renew();

      if (
        locked ||
        started !== generation ||
        isIdle()
      ) {
        lockEnterpriseSession();
        throw new Error("Sesion interrumpida.");
      }

      saveEnterpriseTokens(tokens);
    })();

    refreshPromise = task;

    task.then(
      () => {
        if (refreshPromise === task) refreshPromise = null;
      },
      () => {
        if (refreshPromise === task) refreshPromise = null;
      }
    );
  }

  try {
    await refreshPromise;
  } catch {
    lockEnterpriseSession();

    await waitUntilUnlocked();

    if (expiration() <= Date.now()) {
      throw new Error("Sesion no recuperada.");
    }
  }
}

function protectedApi(config) {
  try {
    const full = axios.getUri(config);
    const url = new URL(full, window.location.origin);

    return (
      url.protocol === "https:" &&
      /^[a-z0-9-]+\.execute-api\.us-east-1\.amazonaws\.com$/i
        .test(url.hostname)
    );
  } catch {
    return false;
  }
}

function eligible(config) {
  return Boolean(
    config &&
    !config.__ipSkipSession &&
    protectedApi(config)
  );
}

function safeRead(config) {
  const method = String(
    config.method || "get"
  ).toLowerCase();

  return method === "get" || method === "head";
}

function authKind(config) {
  const old = String(
    config.headers?.Authorization || ""
  ).replace(/^Bearer\s+/i, "");

  const tokenUse = readClaims(old).token_use;

  if (tokenUse === "access") return "access";
  if (tokenUse === "id") return "id";

  return "id";
}

function updateAuthorization(config) {
  const previous = String(
    config.headers?.Authorization || ""
  );

  const kind = config.__ipAuthKind || authKind(config);
  config.__ipAuthKind = kind;

  const tokens = currentTokens();

  const token =
    kind === "access"
      ? tokens.accessToken
      : tokens.idToken;

  if (!token) throw new Error("Token no disponible.");

  const bearer = /^Bearer\s+/i.test(previous);

  config.headers = config.headers || {};

  config.headers.Authorization =
    bearer ? `Bearer ${token}` : token;

  return config;
}

function blockedError() {
  const error = new Error("SESSION_LOCKED");
  error.code = "IP_DEPOT_SESSION_LOCKED";
  return error;
}

export function installEnterpriseInterceptor(instance) {
  if (!instance || installed.has(instance)) return;

  installed.add(instance);

  instance.interceptors.request.use(async config => {
    if (!eligible(config)) return config;

    if (isIdle()) lockEnterpriseSession();

    await ensureEnterpriseSession();

    if (locked) throw blockedError();

    updateAuthorization(config);

    return config;
  });

  instance.interceptors.response.use(
    response => {
      if (eligible(response.config) && locked) {
        return Promise.reject(blockedError());
      }

      return response;
    },

    async error => {
      const config = error?.config;

      if (!eligible(config)) {
        return Promise.reject(error);
      }

      if (error?.code === "IP_DEPOT_SESSION_LOCKED") {
        return Promise.reject(error);
      }

      if (locked) {
        return Promise.reject(blockedError());
      }

      if (error?.response?.status !== 401) {
        return Promise.reject(error);
      }

      if (config.__ipRetried) {
        return Promise.reject(error);
      }

      if (!safeRead(config)) {
        if (expiration() <= Date.now()) {
          lockEnterpriseSession();
        }

        return Promise.reject(error);
      }

      config.__ipRetried = true;
      config.__ipAuthKind = authKind(config);

      try {
        const previous = String(
          config.headers?.Authorization || ""
        ).replace(/^Bearer\s+/i, "");

        const tokens = currentTokens();

        const latest = config.__ipAuthKind === "access"
          ? tokens.accessToken
          : tokens.idToken;

        // Si otra solicitud ya renovo las credenciales,
        // evitar una segunda renovacion.
        if (previous === latest) {
          await ensureEnterpriseSession(true);
        } else {
          await ensureEnterpriseSession();
        }

        if (locked) throw blockedError();

        updateAuthorization(config);

        return instance.request(config);
      } catch (renewError) {
        if (locked) return Promise.reject(blockedError());

        return Promise.reject(renewError);
      }
    }
  );
}

export function installGlobalEnterpriseInterceptor() {
  installEnterpriseInterceptor(axios);
}